import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Progress, Spin } from 'antd';

import { ILoginState } from '@share/store/slices';
import { getMyReward, getTierBenefits, IWalletRewardState } from "@store/slices";
import { IRewardLevel, IRewardProgress } from '@common-types';

import SideNav from "../../components/dashboard/side-nav/component";
import LevelBenefits from "./level-benefit";
import ProgramInformation from "./program-information";
import { SearchTable } from '@components';
import { TablePaginationConfig } from 'antd/es/table';
import { IColumnMenu, MEMBER_STATUS } from '@constants';
import { InputFilterType } from '@share/common-types';

import "./style.scss";
import { FormattedMessage, useIntl } from 'react-intl';

function AccountRewardComponent() {
  const walletRewardStore = useSelector((state: { walletRewardStore: IWalletRewardState }) => state.walletRewardStore);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [activeTab, setActiveTab] = useState('progress');

  const handleOnTableChange = (pagination: TablePaginationConfig) => {
    if (pagination) {
      setPagination(pagination as any);
    }
  }
  const intl = useIntl();
  const columns: IColumnMenu[] = [
    {
      description: '#',
      key: 'transactionId',
      type: InputFilterType.IntegerNumber,
      width: 60,
      display: true,
      order: 10,
    },
    {
      description: intl.formatMessage({ id: 'description' }),
      key: 'description',
      type: InputFilterType.Text,
      width: 100,
      display: true,
      order: 20,
    },
    {
      description: intl.formatMessage({ id: 'date' }),
      key: 'createdAt',
      type: InputFilterType.Date,
      incomingDateFormat: 'yyyy-MM-DD',
      width: 115,
      display: true,
      order: 30,
    },
  
    {
      description: intl.formatMessage({ id: 'amount' }),
      key: 'amount',
      type: InputFilterType.Currency,
      width: 90,
      display: true,
      order: 40,
    },
    {
      description: intl.formatMessage({ id: 'balance' }),
      key: 'balance',
      type: InputFilterType.Currency,
      width: 90,
      display: true,
      order: 50,
    },
  ];

  function calcPercentage(x: number, y: number) {
    const percent = (x / y) * 100
    return percent;
  }

  const { loading: loading, myReward, tierBenefits, myRewardActivities } = walletRewardStore;

  return (
    <section className="pt-3 account-rewards">
      <div className="container">
        <div className="row">
          <SideNav />

          <div className="col-lg-8 col-xl-9">

            <div className="card shadow-sm border mb-5">
              <div className="card-header bg-transparent border-bottom">
                <h4 className="card-header-title"><i className="bi bi-trophy me-2"></i><FormattedMessage id='my.rewards' /></h4>
              </div>

              <Spin spinning={loading}>
                <div className="card-body vstack gap-4">
                  <div className="row g-3 justify-content-between align-items-top border-bottom mb-1 pb-3">
                    <div className="col-6">
                      <div className={`h3 fw-medium text-tier-${myReward.current.order} mb-0 mt-0`}><i className="bi bi-gem pe-2"></i>{myReward.current.name}</div>
                      <div className="h6 fw-light text-primary small mt-0">{myReward.current.expires}</div>
                    </div>
                    <div className="col-6 vstack align-items-end justify-content-start">
                      <div className={`h4 mb-0 fw-bold text-tier-${myReward.current.order}`}>{Math.floor(myReward.current.point)}<span className="smaller"> pts</span></div>
                      <div className="h5 fw-light small mt-0 text-primary"><FormattedMessage id='value' />: ${Math.floor(myReward.current.value * 100) / 100}</div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <ul className="nav nav-pills-shadow nav-responsive">
                        {myReward?.progress && myReward.rewardLevels.length &&
                          <li className="nav-item">
                            <a
                              className={`nav-link mb-0 me-sm-2 ${activeTab === 'progress' && MEMBER_STATUS.ACTIVE}`}
                              onClick={() => setActiveTab('progress')}
                              id="progress"
                            >
                              <FormattedMessage id="level.progress" />
                            </a>
                          </li>}

                        <li className="nav-item">
                          <a
                            className={`nav-link mb-0 me-sm-2 ${activeTab === 'activity' && MEMBER_STATUS.ACTIVE}`}
                            onClick={() => setActiveTab('activity')}
                            id="activity"
                          >
                            <FormattedMessage id="activity" />
                          </a>
                        </li>
                      </ul>

                      <hr style={{ width: '-webkit-fill-available' }} />

                      <div className="tab-content">
                        {activeTab === 'progress' && myReward?.progress && myReward.rewardLevels.length ?
                          <div className="tab-pane show active">
                            <div className="row mb-4">
                              <div className="col-lg-12">
                                <div className="h4 fw-medium mb-0 mt-0 text-secondary"><FormattedMessage id='next.level' />: <span className={`fw-bold text-tier-${myReward.progress.nextLevelOrder}`}>{myReward.progress.nextLevel}</span></div>
                                <div>{myReward.progress.expirationDescription}</div>
                              </div>
                            </div>
                            <div className="row align-items-center pe-3">
                              { 
                                myReward.progress.progresses.map((rule: IRewardProgress, idx: number) => (
                                  <>
                                    <div className="col-lg-5" key={idx}>
                                      <div className="flex-row g-3 justify-content-between align-items-top hstack">
                                        <div className="h6 fw-bold text-primary pb-2">{rule.ruleType}: </div>
                                        <div className="h6 fw-light pb-2">{rule.userValue} / {rule.ruleValue}</div>
                                      </div>
                                      <Progress percent={calcPercentage(rule.userValue, rule.ruleValue)} />
                                    </div>
                                    {
                                      (myReward.progress.progresses.length - idx) <= 1 ? null :
                                        <div className="col-lg-2 text-center" key={`or_${idx}`}>
                                          <div className="py-3">(or)</div>
                                        </div>
                                    }
                                  </>
                                ))
                              }
                            </div>
                            <hr className="my-4" />
                            <div className="row">
                              <div className="col-lg-12">
                                <div className="row">
                                  <div className="h5"><FormattedMessage id='reward.levels' />:</div>
                                </div>

                                {
                                  myReward.rewardLevels.map((level: IRewardLevel, idx: number) => (
                                    <div className="d-md-flex d-block flex-row g-3 justify-content-between align-items-top hstack my-2" key={idx}>
                                      <div className={`h5 fw-bold text-tier-${level.order}`}><i className={`bi bi-${level.isCurrent ? 'unlock' : 'lock'}`}></i> {level.levelName}:
                                        <span className="h6 fw-light text-secondary"> {level.expirationDescription}</span>
                                      </div>
                                      <div className="h6 fw-light text-secondary">{level.levelRules}</div>
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          </div>: null}

                        {activeTab === 'activity' && (
                          <div className="tab-pane show active">
                            <div className="tab-pane mt-3" id="tab-canceled">
                              {myRewardActivities.items?.length ? 
                                <SearchTable
                                  key={'active'}
                                  title={''}
                                  loading={loading}
                                  allColumns={[...columns]}
                                  dataSource={myRewardActivities.items || []}
                                  pagination={pagination}
                                  filters={{}}
                                  totalOrderCount={myRewardActivities?.total || 0}
                                  rowKey={(row: any) => {
                                    return row ? `${row}` : `${row}`;
                                  } }
                          
                                  hideRefresh={true} 
                                  isFilterApplied={false}
                                  onFiltersChange={() => {}}
                                  onTableChange={handleOnTableChange}
                                  onRefresh={() => {}}
                                  onRowClick={() => {}}
                                  onResetFilters={() => {}}
                                  onApplyFilters={() => {}}
                                  onExport={() => {}}
                                ></SearchTable> :
                                <div className="bg-secondary bg-opacity-10">
                                  <div className="d-flex flex-row py-5">
                                    <div className="vstack my-5">
                                      <div className='mx-auto'><i className="bi bi-suitcase-lg h4 text-secondary"></i></div>
                                      <div className="h5 text-secondary mx-auto fw-medium"><FormattedMessage id='no.activity' /></div>
                                      <div className="h6 text-secondary mx-auto"><FormattedMessage id='no.activity.message' /></div>
                                    </div>
                                  </div>
                                </div>}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </Spin>
            </div>

            <LevelBenefits loading={loading} benefits={tierBenefits} />

            <ProgramInformation />

          </div>

        </div>
      </div>
    </section>
  );
};

const AccountReward = AccountRewardComponent;
export default AccountReward;